import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Box } from '@material-ui/core'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Approval from '@mui/icons-material/Approval'
import { Button } from '../../../../components/common/Buttons'
import { useAlertContext } from '../../../../contexts/AlertContext'
import SpacingContainerBase from '../../../../components/layouts/SpacingContainerBase'
import { Paper, SpacingContainer } from '../../../../components/common/Containers'
import Typography from '../../../../components/common/Typography'
import { ACCOUNT_FEATURES } from '../../../../constants/subscriptionSettings'
import connect from '../connect'
import TagsChip from '../organizationDetails/Tags'

const accountTierLabels = {
  [ACCOUNT_FEATURES.EXPIRED]: 'Expired',
  [ACCOUNT_FEATURES.LIMITED]: 'Limited',
  [ACCOUNT_FEATURES.ADVANTAGE]: 'Advantage',
  [ACCOUNT_FEATURES.PRO]: 'Pro',
  [ACCOUNT_FEATURES.FREE_TRIAL]: 'Free Trial',
  [ACCOUNT_FEATURES.SAP_ADVANTAGE]: 'SAP Advantage',
  [ACCOUNT_FEATURES.APP_MAX]: 'App Max',
  [ACCOUNT_FEATURES.PROCUREMENT_PRO]: 'Procurement Pro',
}

const formatAccountTierLabel = tier => {
  if (!tier) return ''

  if (accountTierLabels[tier]) {
    return accountTierLabels[tier]
  }

  return tier
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

const useStyles = makeStyles(() => ({
  infoText: {
    color: '#1f2933',
    marginLeft: 4,
  },
  tierRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
    padding: '0.9rem 1rem',
    borderRadius: 10,
    backgroundColor: 'rgba(36, 70, 109, 0.04)',
    border: '1px solid rgba(36, 70, 109, 0.12)',
    width: 'fit-content',
  },
  tierLabel: {
    color: '#111827',
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  expirationText: {
    color: '#374151',
    fontSize: '0.95rem',
  },
  buttonSection: {
    borderTop: '1px solid rgba(78, 89, 120, 0.12)',
    padding: '2rem 0 0.5rem 0',
    marginTop: '0.5rem',
  },
}))

const UpdateAccountTierFromSalesforce = ({ detail, updateAccountTier, getDetail }) => {
  const styles = useStyles()
  const { successAlert, errorAlert } = useAlertContext()
  const [busy, setBusy] = useState(false)

  const currentAccountTier = detail?.accountTier || detail?.tier
  const isExpiredTier = currentAccountTier === ACCOUNT_FEATURES.EXPIRED

  const handleClick = async () => {
    if (!detail?.id || busy) return

    try {
      setBusy(true)

      const res = await updateAccountTier({
        organizationId: detail.id,
      })

      const payload = res.payload
      const nextAccountTier = payload?.next?.accountTier
      const nextAccountTierLabel = formatAccountTierLabel(nextAccountTier)

      if (payload?.updated) {
        successAlert(
          `Account tier updated from Salesforce${nextAccountTierLabel ? ` to ${nextAccountTierLabel}` : ''}.`
        )
      } else {
        successAlert(
          `Account tier already matches Salesforce${nextAccountTierLabel ? `: ${nextAccountTierLabel}` : ''}.`
        )
      }

      // The CRM access card reads the tier from the detail, so it follows the refresh.
      if (getDetail) getDetail({ organizationId: detail.id })
    } catch {
      errorAlert('Failed to update account tier from Salesforce.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SpacingContainerBase py={0}>
      <Box mt={4}>
        <Paper component={SpacingContainer} py={3} px="5sm3">
          <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
            <Grid item xs zeroMinWidth>
              <Typography
                style={{ paddingBottom: '1rem', alignItems: 'center', display: 'flex' }}
                variant="h4"
                weight="500"
                fontFamily="lexend"
              >
                <Approval style={{ marginRight: '0.5rem' }} />
                Update Account Tier
              </Typography>
            </Grid>
          </Grid>

          <Box
            style={{
              borderTop: `1px solid rgba(78, 89, 120, 0.12)`,
              padding: '2rem 0 0 0',
            }}
          >
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12}>
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" className={styles.infoText}>
                    Pull the latest tier and expiration values from Salesforce for this organization.
                  </Typography>
                </Box>
              </Grid>

              {currentAccountTier && (
                <Grid item xs={12}>
                  <Box className={styles.tierRow}>
                    <Typography className={styles.tierLabel}>
                      Current Account Tier
                    </Typography>

                    <TagsChip
                      status={currentAccountTier}
                      expired={isExpiredTier}
                    />
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box className={styles.buttonSection}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={busy || !detail?.id}
                    onClick={handleClick}
                  >
                    {busy ? 'Updating...' : 'Update From Salesforce'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </SpacingContainerBase>
  )
}

UpdateAccountTierFromSalesforce.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    accountTier: PropTypes.string,
    tier: PropTypes.string,
  }),
  updateAccountTier: PropTypes.func,
  getDetail: PropTypes.func,
}

export default connect(UpdateAccountTierFromSalesforce)